import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faMicrophone,
	faStop,
	faPlay,
	faPause,
	faTrash,
	faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';
import { useAlertContext } from '../../context/AlertContext';

const MessageInput = ({
	onSendMessage,
	onSendAudio,
	audioInputEnabled,
	sessionId,
	startRecording,
	stopRecording,
	isRecording,
	isUploading,
	recordedAudioBlob,
	onDeleteRecording,
}) => {
	const [message, setMessage] = useState('');
	const [hasRecordedAudio, setHasRecordedAudio] = useState(false);
	const [isPlayingPreview, setIsPlayingPreview] = useState(false);
	const textareaRef = useRef(null);
	const audioRef = useRef(null);

	const { showAlert } = useAlertContext();
	const maxRows = 6;

	// Update hasRecordedAudio when recordedAudioBlob changes
	useEffect(() => {
		setHasRecordedAudio(!!recordedAudioBlob);
		if (recordedAudioBlob && audioRef.current) {
			audioRef.current.src = URL.createObjectURL(recordedAudioBlob);
		}
	}, [recordedAudioBlob]);

	useEffect(() => {
		adjustHeight();
	}, [message]);

	// Cleanup object URL when component unmounts or audio changes
	useEffect(() => {
		return () => {
			if (audioRef.current && audioRef.current.src) {
				URL.revokeObjectURL(audioRef.current.src);
			}
		};
	}, [recordedAudioBlob]);

	const adjustHeight = () => {
		if (!textareaRef.current) return;

		const textarea = textareaRef.current;
		textarea.style.height = 'auto';

		const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight);
		const newHeight = Math.min(lineHeight * maxRows, textarea.scrollHeight);

		textarea.style.height = newHeight + 'px';

		// Add the expanded class if the textarea reaches max rows
		if (textarea.scrollHeight > lineHeight * maxRows) {
			textarea.classList.add('expanded');
		} else {
			textarea.classList.remove('expanded');
		}
	};

	const handleSend = () => {
		if (!message.trim()) return;

		onSendMessage(message);
		setMessage('');

		if (textareaRef.current) {
			textareaRef.current.style.height = 'auto';
		}
	};

	const handleKeyPress = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	const handleRecordMessage = async () => {
		if (!audioInputEnabled) {
			showAlert('Audio input is disabled. Please enable it first.', 'warning');
			return;
		}

		try {
			await startRecording();
		} catch (error) {
			console.error('Error starting recording:', error);
			showAlert(
				'Failed to start recording. Please check microphone permissions.',
				'danger'
			);
		}
	};

	const handlePlayPreview = () => {
		if (!audioRef.current) return;

		if (isPlayingPreview) {
			audioRef.current.pause();
			setIsPlayingPreview(false);
		} else {
			audioRef.current
				.play()
				.then(() => setIsPlayingPreview(true))
				.catch((error) => {
					console.error('Error playing audio:', error);
					showAlert('Failed to play audio preview.', 'danger');
				});
		}
	};

	const handleDeleteRecording = () => {
		if (audioRef.current) {
			audioRef.current.pause();
			if (audioRef.current.src) {
				URL.revokeObjectURL(audioRef.current.src);
			}
		}
		setIsPlayingPreview(false);
		setHasRecordedAudio(false);
		onDeleteRecording();
	};

	const handleSendRecording = () => {
		if (recordedAudioBlob) {
			onSendAudio(recordedAudioBlob);
			setHasRecordedAudio(false);
			setIsPlayingPreview(false);
		}
	};

	// Audio event handlers
	const handleAudioEnded = () => {
		setIsPlayingPreview(false);
	};

	const handleAudioError = () => {
		setIsPlayingPreview(false);
		showAlert('Error playing audio preview.', 'danger');
	};

	return (
		<div id="messageInput" className="d-flex col-md-6">
			<textarea
				id="message"
				ref={textareaRef}
				rows="1"
				className="form-control mr-2"
				placeholder="Enter message"
				value={message}
				onChange={(e) => setMessage(e.target.value)}
				onKeyPress={handleKeyPress}
				disabled={hasRecordedAudio} // Disable text input when audio is recorded
			></textarea>

			{/* Hidden audio element for preview playback */}
			<audio
				ref={audioRef}
				onEnded={handleAudioEnded}
				onError={handleAudioError}
				style={{ display: 'none' }}
			/>

			{/* Text message send button - only show when no recorded audio */}
			{!hasRecordedAudio && (
				<button
					className="btn btn-primary"
					onClick={handleSend}
					disabled={isRecording}
				>
					Send
				</button>
			)}

			{audioInputEnabled && (
				<>
					{/* Record button - show when idle */}
					{!isRecording && !hasRecordedAudio && !isUploading && (
						<button
							className="btn btn-outline-primary ml-2 audio-element"
							id="recordButton"
							onClick={handleRecordMessage}
						>
							<FontAwesomeIcon icon={faMicrophone} />
						</button>
					)}

					{/* Stop recording button - show when recording */}
					{isRecording && (
						<button
							className="btn btn-outline-danger ml-2 audio-element"
							id="stopButton"
							onClick={stopRecording}
						>
							<FontAwesomeIcon icon={faStop} />
						</button>
					)}

					{/* Audio preview controls - show when audio is recorded */}
					{hasRecordedAudio && !isUploading && (
						<>
							<button
								className="btn btn-outline-secondary ml-2 audio-element"
								onClick={handlePlayPreview}
								title={isPlayingPreview ? 'Pause' : 'Play'}
							>
								<FontAwesomeIcon icon={isPlayingPreview ? faPause : faPlay} />
							</button>

							<button
								className="btn btn-outline-danger ml-2 audio-element"
								onClick={handleDeleteRecording}
								title="Delete recording"
							>
								<FontAwesomeIcon icon={faTrash} />
							</button>

							<button
								className="btn btn-primary ml-2 audio-element"
								onClick={handleSendRecording}
								title="Send audio message"
							>
								<FontAwesomeIcon icon={faPaperPlane} />
							</button>
						</>
					)}

					{/* Upload spinner - show when uploading */}
					{isUploading && (
						<button
							className="btn btn-outline-primary ml-2 audio-element"
							id="spinnerButton"
							disabled
						>
							<span
								className="spinner-border spinner-border-sm"
								role="status"
								aria-hidden="true"
							></span>
							<span className="sr-only">Uploading...</span>
						</button>
					)}
				</>
			)}
		</div>
	);
};

export default MessageInput;
